import {
  isLoadedFromHiMarleyConnect,
  detectPlatform,
  PLATFORM,
  type PlatformKey,
  getMobileAppVersion,
} from '@app/helpers/platform';
import { loadZendeskWidget } from '@app/helpers/zendesk/zendesk-widget';

const getPendoKeyForPlatform = (platform: PlatformKey): string | null => {
  if (platform === PLATFORM.IOS) return process.env.PENDO_API_KEY_IOS || null;
  if (platform === PLATFORM.ANDROID)
    return process.env.PENDO_API_KEY_ANDROID || null;
  if (platform === PLATFORM.CONNECT)
    return process.env.PENDO_API_KEY_CONNECT || null;
  return process.env.PENDO_API_KEY_WEB || null;
};

function alreadyInjected(): boolean {
  if (typeof document === 'undefined') return false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if ((window as any).__pendoAgentLoaded) return true;
  return !!document.querySelector('script[src*="cdn.pendo.io/agent/static/"]');
}

function clearPendoLocalStorageIfAnonymous() {
  // Find the _pendo_visitorId key in localStorage
  const visitorIdKey = Object.keys(localStorage).find((key) =>
    key.startsWith('_pendo_visitorId'),
  );

  if (!visitorIdKey) {
    return false; // no visitor ID key found
  }

  const visitorIdValue = localStorage.getItem(visitorIdKey);

  if (!visitorIdValue || !visitorIdValue.includes('ANONYMOUS-')) {
    return false; // nothing to do
  }

  // Clear all Pendo-related localStorage items
  Object.keys(localStorage).forEach((key) => {
    if (
      key.startsWith('_pendo') ||
      key.startsWith('pendo') ||
      key.startsWith('__pendo')
    ) {
      localStorage.removeItem(key);
    }
  });

  return true;
}

export function loadPendoAgent(): void {
  try {
    if (!process?.env?.ENABLE_THIRD_PARTY_SCRIPTS) return;
    if (isLoadedFromHiMarleyConnect()) return;
    if (alreadyInjected()) return;

    const platform = detectPlatform();
    const apiKey = getPendoKeyForPlatform(platform);
    if (!apiKey) return;

    const inline = document.createElement('script');
    inline.type = 'text/javascript';
    inline.text = `(function(apiKey){(function(p,e,n,d,o){var v,w,x,y,z;o=p[d]=p[d]||{};o._q=o._q||[];v=['initialize','identify','updateOptions','pageLoad','track'];for(w=0,x=v.length;w<x;++w)(function(m){o[m]=o[m]||function(){o._q[m===v[0]?'unshift':'push']([m].concat([].slice.call(arguments,0)));};})(v[w]);y=e.createElement(n);y.async=!0;y.src='https://cdn.pendo.io/agent/static/${apiKey}/pendo.js';z=e.getElementsByTagName(n)[0];z.parentNode.insertBefore(y,z);})(window,document,'script','pendo');})('${apiKey}');`;
    inline.onload = function onLoad() {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).__pendoAgentLoaded = true;
    };
    document.head.appendChild(inline);
    const mobileAppVersion = getMobileAppVersion();
    const visitor = {
      platform,
      webAppVersion: process.env.APP_VERSION,
      ...(mobileAppVersion && { mobileAppVersion }),
    };

    clearPendoLocalStorageIfAnonymous();
    /* eslint-disable no-undef */
    pendo.initialize({
      visitor,
      events: { ready: loadZendeskWidget },
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Failed to load Pendo agent', err);
  }
}

export default loadPendoAgent;
